import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {MOLBIO_ANALYTICS_ACTIONS, MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import type {ModelRunSource} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileStates from 'FileStates';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import staticURL from 'lib/staticURL';
import {getScientificAIDisabledMessage} from 'model-hub/permissions/ModelHubPermissions';
import {
  getMaxAminoAcidCount,
  getModelDisplayName,
  getAvailableStructurePredictionModels,
  supportsMultimers,
  getModelPaperLink,
  ScientificModelPreferences,
  resolveInitialStructurePredictionModel,
} from 'model-hub/scientific-models/ScientificModelConstants';
import StickyFooter, {
  STICKY_FOOTER_CONTAINER_STYLES,
  STICKY_FOOTER_CONTENT_STYLES,
} from 'model-hub/StickyFooter';
import {RemainingExecutionsAndTenantNameQuery} from 'molbio/aa-sequences/alphafold/__generated/RemainingExecutionsAndTenantNameQuery';
import {UserVerificationStatusQuery} from 'molbio/aa-sequences/alphafold/__generated/UserVerificationStatusQuery';
import AlphaFoldTabState from 'molbio/aa-sequences/alphafold/AlphaFoldTabState';
import {
  ACADEMIC_VERIFICATION_MESSAGE,
  isAcademicUserBlocked,
  REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY,
  requestRun,
  USER_VERIFICATION_STATUS,
} from 'molbio/aa-sequences/alphafold/AlphaFoldTabUtils';
import {ViewState} from 'molbio/aa-sequences/alphafold/AlphaFoldTabView';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import notifications from 'notifications';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE, GRAY_DARKER} from 'ui-library/Colors';
import usePreference from 'ui-library/hooks/usePreference';
import {CenteredSpinner} from 'ui-library/Spinner';

import ComponentSelector from './ComponentSelector';
import {
  ComponentValidationResult,
  flattenComponentSelectionToComponentIds,
  getComponentValidationResult,
  getValidationErrorMessages,
  SelectedComponents,
} from './ComponentUtils';
import ModelSelector from './ModelSelector';

function makePrimaryComponentSelection(
  proteinId: string,
  aminoAcids: string | undefined,
  permissions: Permissions | undefined
): SelectedComponents {
  return {
    [proteinId]: {
      aminoAcids: aminoAcids ?? '',
      count: 1,
      numHeavyAtoms: 0,
      entityType: ModelTypeConstants.PROTEIN,
      permissions: {
        readable: permissions?.readable ?? false,
        polymerFeaturesEditable: permissions?.polymerFeaturesEditable ?? false,
      },
    },
  };
}

const navigateToProtein = async (proteinId: string): Promise<void> => {
  // Close sidepanel before navigating to avoid showing blank sidepanel
  // Use autoOpenFile: false since we're already opening a specific file
  SidepanelActions.close({autoOpenFile: false});

  const fileState = await FileStates.get().openFolderItem(proteinId, {open: true});
  if (!fileState) {
    return;
  }
  const existingTab = fileState.findTab(AlphaFoldTabState);
  if (existingTab != null) {
    existingTab.set({active: true, initialView: ViewState.COORDINATE_STRUCTURES_LIST});
  }
};

const submitStructurePrediction = async (
  inputProteinId: string | null,
  selectedComponents: SelectedComponents,
  selectedModel: string,
  setSubmitting: (submitting: boolean) => void,
  remainingExecutionsAndTenantNameData: RemainingExecutionsAndTenantNameQuery,
  onSubmit?: () => void,
  source?: ModelRunSource
): Promise<void> => {
  setSubmitting(true);
  try {
    const {proteinIds, moleculeIds} = flattenComponentSelectionToComponentIds(selectedComponents);
    const primaryProteinId = inputProteinId ?? proteinIds[0];
    if (!primaryProteinId) {
      throw new Error('No primary protein id found');
    }
    await requestRun(
      proteinIds,
      moleculeIds,
      primaryProteinId,
      remainingExecutionsAndTenantNameData,
      selectedModel,
      source
    );
    trackEvent(MOLBIO_ANALYTICS_ACTIONS.REQUESTED_ALPHAFOLD_RUN);
    if (inputProteinId == null) {
      await navigateToProtein(primaryProteinId);
    }
    onSubmit?.();
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (error) {
    notifications.error('Error when submitting structure prediction. Please try again.');
  } finally {
    setSubmitting(false);
  }
};

export interface StructurePredictionFormProps {
  inputProteinId?: string;
  inputAminoAcids?: string;
  inputPermissions?: Permissions;
  startingModel?: string;
  onRunAllModels?: () => Promise<void>;
  onSubmit?: () => void;
  onBack?: () => void;
  /** Identifies which UI surface triggered this form (protein tab vs model hub). */
  source?: ModelRunSource;
}

export default function StructurePredictionForm({
  inputProteinId,
  inputAminoAcids,
  inputPermissions,
  startingModel,
  onRunAllModels,
  onSubmit,
  onBack,
  source,
}: StructurePredictionFormProps): JSX.Element {
  const [submitting, setSubmitting] = useState<boolean>(false);
  // If an input model is provided, use it. Otherwise, use the default structure prediction model
  // as the start and allow the user to change the model.
  const showModelSelector =
    startingModel == null && BenchlingConfig.MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS;
  const [lastSelectedModel, setLastSelectedModel] = usePreference<string | undefined>(
    ScientificModelPreferences.LAST_SELECTED_STRUCTURE_PREDICTION_MODEL
  );
  const availableModels = getAvailableStructurePredictionModels();
  const [selectedModel, setSelectedModel] = useState<string>(
    resolveInitialStructurePredictionModel(availableModels, startingModel, lastSelectedModel)
  );

  const needsComponentSelection = inputProteinId == null;
  const remainingExecutionsAndTenantNameQueryResult =
    useBenchlingQuery<RemainingExecutionsAndTenantNameQuery>(REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY);
  const userVerificationStatusQueryResult =
    useBenchlingQuery<UserVerificationStatusQuery>(USER_VERIFICATION_STATUS);

  const [selectedComponents, setSelectedComponents] = useState<SelectedComponents>(
    !needsComponentSelection
      ? makePrimaryComponentSelection(inputProteinId, inputAminoAcids, inputPermissions)
      : {}
  );

  const [validationResult, setValidationResult] = useState<ComponentValidationResult | null>(null);
  useEffect(() => {
    const result = getComponentValidationResult(selectedComponents, selectedModel);
    setValidationResult(result);
  }, [selectedComponents, selectedModel]);

  // Sync selectedComponents when inputProteinId or store data change: clear when no primary,
  // reset to new primary when it changes, or update amino acids/permissions when they change in the store
  useEffect(() => {
    setSelectedComponents((prevComponents) => {
      if (inputProteinId == null) {
        return {};
      }
      const primary = prevComponents[inputProteinId];
      if (primary) {
        const nextAminoAcids = inputAminoAcids ?? '';
        const nextPermissions = {
          readable: inputPermissions?.readable ?? false,
          polymerFeaturesEditable: inputPermissions?.polymerFeaturesEditable ?? false,
        };
        const aminoAcidsChanged = primary.aminoAcids !== nextAminoAcids;
        const permissionsChanged =
          primary.permissions.readable !== nextPermissions.readable ||
          primary.permissions.polymerFeaturesEditable !== nextPermissions.polymerFeaturesEditable;
        if (aminoAcidsChanged || permissionsChanged) {
          return {
            ...prevComponents,
            [inputProteinId]: {
              ...primary,
              ...(aminoAcidsChanged && {aminoAcids: nextAminoAcids}),
              ...(permissionsChanged && {permissions: nextPermissions}),
            },
          };
        }
        return prevComponents;
      }
      return makePrimaryComponentSelection(inputProteinId, inputAminoAcids, inputPermissions);
    });
  }, [inputAminoAcids, inputProteinId, inputPermissions]);

  if (
    remainingExecutionsAndTenantNameQueryResult.type === QueryResultType.PENDING ||
    userVerificationStatusQueryResult.type === QueryResultType.PENDING
  ) {
    return <CenteredSpinner />;
  } else if (
    remainingExecutionsAndTenantNameQueryResult.type === QueryResultType.ERROR ||
    userVerificationStatusQueryResult.type === QueryResultType.ERROR
  ) {
    notifications.error('Error when loading structure prediction form. Please refresh the page.');
    return <></>;
  }

  const currentUser = userVerificationStatusQueryResult.data.currentUser;
  assert(
    currentUser?.__typename === 'UserNode' || currentUser == null,
    `Non-user should not access this page.`
  );

  let blockingBannerMessage: string | null = getScientificAIDisabledMessage();
  if (blockingBannerMessage == null) {
    if (isAcademicUserBlocked(currentUser)) {
      blockingBannerMessage = ACADEMIC_VERIFICATION_MESSAGE;
    } else if (remainingExecutionsAndTenantNameQueryResult.data.remainingAlphafoldExecutions === 0) {
      blockingBannerMessage = 'You do not have any remaining structure prediction runs.';
    } else if (validationResult != null) {
      // Show validation banner for any selection size (0, 1, or 2+ components); wording depends on isSingleComponent
      const isSingleComponent =
        inputProteinId != null &&
        Object.keys(selectedComponents).length === 1 &&
        selectedComponents[inputProteinId]?.count === 1;
      blockingBannerMessage = getValidationErrorMessages(validationResult, isSingleComponent).join('\n');
    }
  }
  return (
    <div className={css(styles.outerContainer)}>
      <div className={css(styles.content)}>
        {showModelSelector && onRunAllModels && !blockingBannerMessage && (
          <div className={css(styles.largeInlineBannerMargin)}>
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              hideIcon={true}
              text={`Not sure which model to run? Compare them by running all ${getAvailableStructurePredictionModels().length}.`}
              action={{
                actionType: InlineBannerActionType.CUSTOM,
                actionText: submitting ? 'Submitting...' : 'Run All',
                actionOnClick: async () => {
                  if (submitting) {
                    return;
                  }
                  trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.CLICKED_STRUCTURE_PREDICTION_RUN_ALL_MODELS, {
                    actionOrigin: ActionOrigin.MODEL_HUB_STRUCTURE_PREDICTION_FORM_PAGE,
                  });
                  setSubmitting(true);
                  try {
                    await onRunAllModels?.();
                  } finally {
                    setSubmitting(false);
                    onSubmit?.();
                  }
                },
                buttonStyle: submitting ? ButtonStyle.TEXT : ButtonStyle.PRIMARY,
                rightAlign: true,
              }}
            />
          </div>
        )}

        <div className={css(styles.contentPadding)}>
          {blockingBannerMessage != null && (
            <div className={css(styles.largeInlineBannerMargin)}>
              <InlineBanner text={blockingBannerMessage} bannerType={InlineBannerType.WARNING} />
            </div>
          )}
          {blockingBannerMessage == null && <div className={css(styles.pseudoBanner)} />}
          <div className={css(styles.centered, styles.imgMargin)}>
            <img
              width={255}
              height={175}
              src={staticURL('/static/img/alphafold_example_quality_structure.png')}
            />
          </div>
          <p className={css(styles.largestText, styles.centered)}>Predict 3D structure</p>
          <p className={css(styles.smallerText, styles.grayDarker, styles.centered)}>
            Predict structures of proteins up to {getMaxAminoAcidCount(selectedModel)} residues using&nbsp;
            {getModelDisplayName(selectedModel)}
          </p>
        </div>

        {showModelSelector && (
          <div className={css(styles.centered, styles.modelSelectorContainer)}>
            <ModelSelector
              selectedModel={selectedModel}
              onModelChange={(model) => {
                setSelectedModel(model);
                setLastSelectedModel(model);
                if (inputProteinId != null) {
                  setSelectedComponents(
                    makePrimaryComponentSelection(inputProteinId, inputAminoAcids, inputPermissions)
                  );
                } else {
                  setSelectedComponents({});
                }
              }}
              disabled={false}
              className={css(styles.modelSelector)}
            />
          </div>
        )}

        <div className={css(styles.centered)}>
          <a
            href={getModelPaperLink(selectedModel)}
            target="_blank"
            rel="noopener noreferrer"
            className={css(styles.modelLink)}
          >
            Learn more about {getModelDisplayName(selectedModel)}
          </a>
        </div>

        {(supportsMultimers(selectedModel) || needsComponentSelection) && (
          <ComponentSelector
            selectedComponents={selectedComponents}
            onComponentsChange={setSelectedComponents}
            primaryProteinId={inputProteinId ?? null}
            model={selectedModel}
          />
        )}
      </div>
      <StickyFooter>
        {onBack && (
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.MEDIUM}
            text="Cancel"
            onClick={onBack}
          />
        )}
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.MEDIUM}
          text={submitting ? 'Submitting...' : 'Predict 3D structure'}
          onClick={async () => {
            await submitStructurePrediction(
              inputProteinId ?? null,
              selectedComponents,
              selectedModel,
              setSubmitting,
              remainingExecutionsAndTenantNameQueryResult.data,
              onSubmit,
              source
            );
          }}
          disabled={validationResult != null || submitting || blockingBannerMessage != null}
          {...getTestProps({component: 'StructurePredictionForm', element: 'submitButton'})}
        />
      </StickyFooter>
    </div>
  );
}

const styles = StyleSheet.create({
  outerContainer: STICKY_FOOTER_CONTAINER_STYLES,
  content: STICKY_FOOTER_CONTENT_STYLES,
  contentPadding: {
    padding: '20px',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  title: {
    ...FONT_PROXIMA_17,
    fontWeight: 600,
    color: GRAY_DARKER,
  },
  description: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginTop: '8px',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
  },
  imgMargin: {
    margin: '34px 0px',
  },
  largestText: FONT_PROXIMA_17,
  smallerText: FONT_PROXIMA_13,
  grayDarker: {
    color: GRAY_DARKER,
  },
  modelSelectorContainer: {
    margin: '20px 0px',
  },
  modelSelector: {
    minWidth: '400px',
    textAlign: 'left',
    padding: '0px 16px',
  },
  largeInlineBannerMargin: {
    margin: '18px 11px',
  },
  pseudoBanner: {
    height: '35.5px',
    margin: '18px 11px',
  },
  modelLink: {
    ...FONT_PROXIMA_13,
    color: BLUE,
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});
